// js/sections/secondary-navigation.js

const observer = function observeElementAtTop(el, offset = "0px 0px 0px 0px", callback, threshold = 0) {
  let margin = offset.includes(" ") ? offset : `-${offset}px 0px 0px 0px`;
  const observer = new IntersectionObserver(
    ([entry]) => {
      callback(entry);
    },
    {
      root: null,
      rootMargin: margin,
      threshold: threshold,
    }
  );

  observer.observe(el);
  return observer;
};

function isView(element, callback, options = {}) {
  const { margin = "0px", amount = 0, root = null, once = false } = options;

  let hasTriggered = false;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const isInView = entry.intersectionRatio >= amount;

        if (once && hasTriggered && !isInView) {
          return;
        }

        if (isInView && once) {
          hasTriggered = true;
        }

        callback(isInView, entry);
      });
    },
    {
      root,
      rootMargin: margin,
      threshold: typeof amount === "number" ? [amount] : amount,
    }
  );

  observer.observe(element);

  // 返回清理函数，用于停止观察
  return () => observer.unobserve(element);
}

var SecondaryNavigation2 = class extends HTMLElement {
  constructor() {
    super();
    this.menuItems = [];
    this.currentIndex = 0;
    this._scrollHandler = null;
    this._headerContainer = null;
    this._sectionWrapper = null;
    this._lastScrollY = 0;
    this._isHeaderVisible = true;
    this._product2508Observer = null;
    this._isSticky = false; // 二级导航是否固定在顶部
    this._stickyObserver = null;
    this._sectionRect = null; // 二级导航的位置信息
    this._isManualScrolling = false; // 是否正在手动切换（点击菜单项）
    this._manualScrollTimer = null; // 手动滚动计时器
  }

   connectedCallback() {
    this._sectionWrapper = this.closest(".secondary-navigation-2511");
    this._init();
    setTimeout(() => {
      this._setupScrollListener();
    }, 1000);
    this._setupEventListeners();
    this._setupStickyObserver(); // 监听二级导航是否到达顶部
  }
  disconnectedCallback() {
    // 清理所有监听器
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }
    if (this._product2508Observer) {
      this._product2508Observer.disconnect();
    }
    if (this._stickyObserver) {
      this._stickyObserver.disconnect();
    }
    if (this._manualScrollTimer) {
      clearTimeout(this._manualScrollTimer);
    }
  }

  _init() {
    // 获取所有菜单项配置
    this.menuItems = Array.from(this.querySelectorAll(".secondary-nav-item")).map((item) => ({
      element: item,
      title: item.getAttribute("data-title") || item.textContent.trim(),
      targetId: item.getAttribute("data-target-id"),
      targetElement: null,
    }));

    // 查找对应的目标元素
    this.menuItems.forEach((item) => {
      if (item.targetId) {
        item.targetElement = document.getElementById(item.targetId);
      }
    });

    // 获取一级菜单容器
    this._headerContainer = document.querySelector(".header-container");
    if (this._headerContainer) {
      this.dataset.headerHeight = this._headerContainer.getBoundingClientRect().height;
      this._setupProduct2508Observer();
    }

    // 先立即激活第一个，确保有默认激活状态
    this._updateActiveItem(0);
    this._scrollToActiveMenuItem(0);

    // 使用 setTimeout 确保页面布局完成后再重新计算正确的激活状态
    setTimeout(() => {
      this._updateActiveState(true); // 强制更新，确保滚动位置正确
    }, 100);
  }

  _setupScrollListener() {
    this._scrollHandler = this._throttle(() => {
      this._handleHeaderVisibility();
    }, 50);

    window.addEventListener("scroll", this._scrollHandler, { passive: true });

    this._resizeHandler = this._throttle(() => {
      this._handleHeaderVisibilityResize();
    }, 50);
    window.addEventListener("resize", this._resizeHandler, { passive: true });
  }

  _handleHeaderVisibilityResize() {
    this._headerContainer = document.querySelector(".header-container");
    if (this._headerContainer) {
      // 更新 headerHeight
      this.dataset.headerHeight = this._headerContainer.getBoundingClientRect().height;

      // 重新设置 observers
      this._setupProduct2508Observer();
      this._setupStickyObserver();

      // 重新计算位置
      this._handleHeaderVisibility();
    }
  }

  _setupProduct2508Observer() {
    // 先清理现有的 observer
    if (this._product2508Observer) {
      this._product2508Observer.disconnect();
    }

    const product2508 = document.querySelector(".product-2508");
    if (product2508) {
      this._product2508Observer = observer(product2508, this.dataset.headerHeight, (entry) => {
        this.isIntersecting = entry.isIntersecting;
      });
    }
  }

  /**
   * 监听二级导航是否到达顶部（sticky 生效）
   */
  _setupStickyObserver() {
    if (!this._sectionWrapper) return;

    // 清理旧的观察器
    if (this._stickyObserver) {
      this._stickyObserver.disconnect();
    }

    // 监听二级导航的位置 - 使用视口中心点（50vh）作为判断标准
    this._stickyObserver = new IntersectionObserver(
      ([entry]) => {
        // 记录二级导航的位置状态，用于后续判断
        this._sectionRect = entry.boundingClientRect;
      },
      {
        threshold: [0, 0.1, 0.5, 1],
        rootMargin: "0px",
      }
    );

    this._stickyObserver.observe(this._sectionWrapper);
  }

  _setupEventListeners() {
    this.menuItems.forEach((item, index) => {
      item.element.addEventListener("click", (e) => {
        e.preventDefault();
        this._scrollToTarget(index);
      });
    });
  }

  /**
   * 更新菜单激活状态
   * 找到视口中心线所在的目标元素
   */
  _updateActiveState(forceUpdate = false) {
    // 如果正在手动切换（点击菜单项跳转中），跳过自动更新，避免中间过渡激活
    if (this._isManualScrolling && !forceUpdate) {
      return;
    }

    // 视口中心线
    const viewportCenter = window.innerHeight / 2;

    let targetIndex = -1; // 中心线所在的元素索引

    // 遍历所有菜单项，找到视口中心线穿过的元素
    for (let i = 0; i < this.menuItems.length; i++) {
      const item = this.menuItems[i];
      if (item.targetElement) {
        const rect = item.targetElement.getBoundingClientRect();

        // 判断视口中心线是否在元素范围内
        // 即元素的顶部 <= 中心线 <= 元素的底部
        if (rect.top <= viewportCenter && rect.bottom >= viewportCenter) {
          targetIndex = i;
          break; // 找到第一个包含中心线的元素就停止
        }
      }
    }

    // 如果没有找到包含中心线的元素
    if (targetIndex === -1) {
      // 不更新激活状态，保持当前状态，避免滚动过程中的闪烁
      // 除非是强制更新（如初始化时）
      if (forceUpdate) {
        targetIndex = 0; // 初始化时默认第一个
      } else {
        return; // 保持当前激活状态不变
      }
    }

    // 如果激活状态发生变化，或者强制更新，则更新并滚动到对应菜单项
    if (forceUpdate || this.currentIndex !== targetIndex) {
      this.currentIndex = targetIndex;
      this._updateActiveItem(targetIndex);
      this._scrollToActiveMenuItem(targetIndex);
    }
  }

  /**
   * 滚动二级导航，使激活的菜单项可见
   * 如果导航可以横向滚动，将激活的菜单项滚动到中心位置
   */
  _scrollToActiveMenuItem(index) {
    const item = this.menuItems[index];
    if (!item || !item.element) return;

    // 获取二级导航的容器（尝试多个可能的选择器）
    const navContainer =
      this.querySelector(".secondary-nav-list") ||
      this.querySelector(".secondary-nav-container") ||
      this.querySelector(".secondary-nav-wrapper") ||
      this.querySelector("[class*='nav-list']") ||
      this.querySelector("[class*='nav-container']") ||
      this;

    // 检查容器是否存在且可以横向滚动
    if (!navContainer) return;

    const canScroll = navContainer.scrollWidth > navContainer.clientWidth;

    if (canScroll) {
      // 计算需要滚动的距离
      // 目标：将菜单项滚动到容器的中心位置

      // 获取元素相对于容器的位置
      const itemRect = item.element.getBoundingClientRect();
      const containerRect = navContainer.getBoundingClientRect();

      // 计算元素相对于容器的偏移
      const itemRelativeLeft = itemRect.left - containerRect.left + navContainer.scrollLeft;
      const itemWidth = itemRect.width;
      const containerWidth = navContainer.clientWidth;

      // 计算菜单项中心和容器中心
      const itemCenter = itemRelativeLeft + itemWidth / 2;
      const containerCenter = containerWidth / 2;

      // 计算目标滚动位置
      let scrollTarget = itemCenter - containerCenter;

      // 边界检查：确保不会滚动到超出范围
      const maxScroll = navContainer.scrollWidth - containerWidth;
      scrollTarget = Math.max(0, Math.min(scrollTarget, maxScroll));

      // 平滑滚动到目标位置
      navContainer.scrollTo({
        left: scrollTarget,
        behavior: "smooth",
      });
    }
  }

  /**
   * 处理一级菜单的显示/隐藏
   * 基于二级导航相对于视口中心点（50vh）的位置来控制
   */
  _handleHeaderVisibility() {
    if (!this._sectionWrapper) return;
    this._headerContainer = document.querySelector(".header-container");
    const _headerContainerHiehgt = this._headerContainer.getBoundingClientRect().height;
    if (this._headerContainer && this.dataset.headerHeight != _headerContainerHiehgt) {
      // 更新 headerHeight
      this.dataset.headerHeight = this._headerContainer.getBoundingClientRect().height;
    }
    const currentScrollY = window.scrollY;
    const scrollDirection = currentScrollY > this._lastScrollY ? "down" : "up";

    // 获取视口高度的50%（中心点）
    const viewportCenter = window.innerHeight / 2;

    // 获取二级导航相对于视口顶部的距离
    const rect = this._sectionWrapper.getBoundingClientRect();
    const sectionTopToViewport = rect.top;

    // 更新菜单激活状态（找到视口中心线所在的目标元素）
    this._updateActiveState();

    /**
     * 三种场景的判断：
     * 场景3: 如果二级导航距离顶部 > 50vh → header显示，二级导航不吸顶（保持自然位置）
     * 场景1: 二级导航距离顶部 <= 50vh + 下滑 → header隐藏，二级导航吸顶(top:0)
     * 场景2: 二级导航距离顶部 <= 50vh + 上滑 → header显示，二级导航跟随header
     */

    if (sectionTopToViewport > viewportCenter) {
      // 场景3: 二级导航还在视口下半部分（距离顶部 > 50vh）
      // header始终显示，二级导航保持自然位置
      this._isSticky = false;
      this._showHeader();
      this._sectionWrapper.style.top = this.dataset.headerHeight + "px";
    } else {
      // 二级导航已经过了视口中心点（距离顶部 <= 50vh）
      this._isSticky = true;

      if (scrollDirection === "down") {
        // 场景1: 下滑 → 隐藏header，二级导航贴顶
        this._hideHeader();
        this._sectionWrapper.style.top = "0px";
      } else {
        // 场景2: 上滑 → 显示header，二级导航跟随header
        this._showHeader();
        this._sectionWrapper.style.top = this.dataset.headerHeight + "px";
      }
    }

    this._lastScrollY = currentScrollY;
  }

  _hideHeader() {
    if (this._headerContainer) {
      this._headerContainer.style.transform = "translateY(-100%)";
      this._headerContainer.style.transition = "transform 0.3s ease";
      this._isHeaderVisible = false;
    }
  }

  _showHeader() {
    if (this._headerContainer) {
      this._headerContainer.style.transform = "translateY(0)";
      this._headerContainer.style.transition = "transform 0.3s ease";
      this._isHeaderVisible = true;
    }
  }

  /**
   * 滚动到指定目标
   */
  _scrollToTarget(index) {
    const item = this.menuItems[index];
    if (!item || !item.targetElement) return;

    // 设置手动滚动标志，禁用自动激活更新
    this._isManualScrolling = true;

    // 清除之前的计时器
    if (this._manualScrollTimer) {
      clearTimeout(this._manualScrollTimer);
    }

    // 立即更新当前索引并滚动二级导航
    this.currentIndex = index;
    this._updateActiveItem(this.currentIndex);
    this._scrollToActiveMenuItem(index);

    // 开始滚动到目标元素
    item.targetElement.scrollIntoView({ behavior: "smooth", block: "start" });

    // 滚动完成后（预估1秒）恢复自动激活更新
    this._manualScrollTimer = setTimeout(() => {
      this._isManualScrolling = false;
      // 滚动完成后强制更新一次，确保激活状态正确
      this._updateActiveState(false);
    }, 1000);
  }

  /**
   * 更新激活状态
   */
  _updateActiveItem(index) {
    this.menuItems.forEach((item, i) => {
      item.element.classList.toggle("active", i === index);
      item.element.setAttribute("aria-current", String(i === index));
    });
  }

  /**
   * 节流函数
   */
  _throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }
};

// 注册二级导航组件
if (!window.customElements.get("secondary-navigation-2511")) {
  window.customElements.define("secondary-navigation-2511", SecondaryNavigation2);
}
